import { ZoneDataRow } from '../models/zone-data-row';
import { temperatureFromShortF32, ZoneFold, ZoneFoldRefused, ZoneHeatInput, ZoneParams } from './zone-fold';

/**
 * Zones derived in the browser, fed by `GetTemperatureData … &raw=1`.
 *
 * CONTRACT: raw-temperature-CONTRACT.md, relayed to DataServices on 2026-09-24.
 * With `raw=1` the server returns the STORED ushorts: not converted, not
 * decimated, sliced exactly to `[windowStart, windowEnd)`. It echoes `RAW: 1`
 * and the applied `RWS`. CT, OT and HeatInput come back in Celsius whatever
 * `unitCode` says.
 *
 * Everything here REFUSES rather than approximates. A response that does not
 * prove it is raw, a row that is decimated or of the wrong width, or a tick that
 * does not strictly follow the last one poisons the derivation. The strip then
 * says zones are unavailable and why. A plausible wrong zone is the failure
 * this exists to prevent.
 */

/** One raw row, as the contract returns it. */
export interface RawTemperatureRow {
  E: number;
  UT?: number;
  T?: string;
  TR: number[];
  SIX?: number[];
}

/** The raw response: today's GetTemperatureData shape plus the raw echoes. */
export interface RawTemperatureResponse {
  RAW?: number;
  RWS?: number;
  TRC: RawTemperatureRow[];
  CT: number;
  OT: number;
  MI: number;
  HeatInput?: Array<{ Temperature?: number; temperature?: number; Minutes?: number; minutes?: number }>;
  heatInput?: Array<{ Temperature?: number; temperature?: number; Minutes?: number; minutes?: number }>;
  HWM?: number;
}

export interface LinerWindow {
  /** Inclusive sample index. */
  start: number;
  /** Exclusive sample index. */
  end: number;
}

/** Throws ZoneFoldRefused unless `resp` is provably the raw slice we asked for. */
export function assertRawResponse(resp: RawTemperatureResponse | null, window: LinerWindow): void {
  if (!resp) {
    throw new ZoneFoldRefused('The server returned no temperature payload.');
  }
  if (resp.RAW !== 1) {
    throw new ZoneFoldRefused(
      'The server did not confirm raw temperatures (RAW is not 1). This DataServices build ' +
        'does not support raw=1 yet, so zones cannot be derived exactly.'
    );
  }
  if (resp.RWS !== window.start) {
    throw new ZoneFoldRefused(
      `The server applied window start ${resp.RWS}, but ${window.start} was requested.`
    );
  }
  for (const row of resp.TRC ?? []) {
    if (row.SIX && row.SIX.length > 0) {
      throw new ZoneFoldRefused('A raw row arrived decimated (it carries SIX).');
    }
  }
}

/** The fold parameters, from the raw response's own Celsius values. */
export function zoneParamsFromRaw(resp: RawTemperatureResponse): ZoneParams {
  const stages = resp.HeatInput ?? resp.heatInput ?? [];
  const heatInputs: ZoneHeatInput[] = stages.map((h) => ({
    temperatureC: Number(h.Temperature ?? h.temperature),
    minutes: Number(h.Minutes ?? h.minutes),
  }));
  return {
    curingTemperatureC: resp.CT,
    overheatTemperatureC: resp.OT,
    measurementIntervalS: resp.MI,
    heatInputs,
  };
}

/**
 * Raw values per chunk request: about 1.3-1.6 MB of JSON at ~5-6 chars per stored
 * ushort. Joel's bandwidth and heap constraint: raw full width for a 630-tick
 * project is ~27.5 MB and ran bulk's tab out of memory twice. So the liner window
 * is fetched in spatial chunks and each chunk's raw rows are dropped once folded.
 */
export const RAW_VALUES_PER_REQUEST = 250_000;

/** A chunk never narrower than this, so a very long cure is not hundreds of requests. */
export const MIN_CHUNK_SAMPLES = 32;

/** Split `[start, end)` into chunks sized so ticks x chunk width stays within the budget. */
export function chunkWindow(window: LinerWindow, expectedTicks: number): LinerWindow[] {
  const width = window.end - window.start;
  const ticks = Math.max(1, Math.floor(expectedTicks) || 1);
  const size = Math.max(MIN_CHUNK_SAMPLES, Math.min(width, Math.floor(RAW_VALUES_PER_REQUEST / ticks)));
  const out: LinerWindow[] = [];
  for (let s = window.start; s < window.end; s += size) {
    out.push({ start: s, end: Math.min(window.end, s + size) });
  }
  return out;
}

interface Chunk {
  window: LinerWindow;
  fold: ZoneFold | null;
}

/**
 * One project's derivation, over one liner window, folded in spatial chunks.
 *
 * WHY CHUNKS ARE EXACT: a sample's zone depends only on that sample's own
 * history, so folding [a, b) and [b, c) separately gives exactly what folding
 * [a, c) gives. Each chunk's history lives in its fold as transition ticks,
 * which is O(width), so a chunk's raw rows can be dropped the moment it is folded.
 *
 * A tick's row is emitted only once EVERY chunk has folded it. The chunks must
 * agree on each tick's timestamp; a disagreement means a live tick landed
 * between two chunk fetches in a way the overlap rule cannot reconcile, and the
 * derivation refuses rather than splicing two different ticks into one row.
 */
export class ZoneDerivation {
  private readonly chunks: Chunk[];
  private refusal: string | null = null;
  private emitted = 0;
  /** Absolute sample indices of the window, shared by every emitted row. */
  private readonly sampleIndices: number[];
  /**
   * Rows skipped because their tick was already folded: a poll that overlaps
   * what an earlier response already folded. The cloud keys telemetry on the tick
   * timestamp, so the same E is the same record. Counted, never silent.
   */
  overlapSkipped = 0;

  constructor(readonly project: string, readonly window: LinerWindow, chunks?: LinerWindow[]) {
    if (!(window.end > window.start)) {
      throw new ZoneFoldRefused(`Liner window [${window.start}, ${window.end}) is empty.`);
    }
    this.sampleIndices = Array.from({ length: window.end - window.start }, (_, k) => window.start + k);
    this.chunks = (chunks && chunks.length ? chunks : [window]).map((w) => ({ window: w, fold: null }));
  }

  get refused(): string | null {
    return this.refusal ?? this.chunks.find((c) => c.fold?.refused)?.fold?.refused ?? null;
  }

  /** Ticks every chunk has folded. */
  get ticks(): number {
    return Math.min(...this.chunks.map((c) => c.fold?.tickCount ?? 0));
  }

  get chunkWindows(): LinerWindow[] {
    return this.chunks.map((c) => c.window);
  }

  /**
   * Fold ONE chunk's raw response (the backfill path). Emits nothing; call
   * emitNewRows() once the chunks are in. Throws ZoneFoldRefused.
   */
  ingestChunk(index: number, resp: RawTemperatureResponse): void {
    const chunk = this.chunks[index];
    this.guard(() => {
      assertRawResponse(resp, chunk.window);
      this.foldRows(chunk, resp, (row) => row.TR);
    });
  }

  /**
   * Fold a WHOLE-window response (the realtime path: a few new ticks), split
   * across the chunks, and return the rows that are now complete.
   */
  ingest(resp: RawTemperatureResponse): ZoneDataRow[] {
    this.guard(() => {
      assertRawResponse(resp, this.window);
      for (const chunk of this.chunks) {
        const a = chunk.window.start - this.window.start;
        const b = chunk.window.end - this.window.start;
        this.foldRows(chunk, resp, (row) => {
          if (!row.TR || row.TR.length !== this.window.end - this.window.start) {
            // Pass it through unsliced so the fold refuses it on width.
            return row.TR ?? [];
          }
          return row.TR.slice(a, b);
        });
      }
    });
    return this.emitNewRows();
  }

  /** The rows for ticks every chunk has now folded, in tick order. */
  emitNewRows(): ZoneDataRow[] {
    return this.guard(() => {
      const upTo = this.ticks;
      const out: ZoneDataRow[] = [];
      for (let t = this.emitted; t < upTo; t++) {
        const epoch = this.chunks[0].fold!.epochAt(t);
        const row: number[] = [];
        for (const chunk of this.chunks) {
          if (chunk.fold!.epochAt(t) !== epoch) {
            throw new ZoneFoldRefused(
              `Chunks disagree on tick ${t} (${epoch} vs ${chunk.fold!.epochAt(t)}); a zone row cannot be assembled from two different ticks.`
            );
          }
          const part = chunk.fold!.zoneRowAt(t);
          for (let k = 0; k < part.length; k++) row.push(part[k]);
        }
        out.push({ ZR: row, SIX: this.sampleIndices });
      }
      this.emitted = upTo;
      return out;
    });
  }

  private foldRows(
    chunk: Chunk,
    resp: RawTemperatureResponse,
    valuesOf: (row: RawTemperatureRow) => number[]
  ): void {
    if (!chunk.fold) {
      chunk.fold = new ZoneFold(chunk.window.end - chunk.window.start, zoneParamsFromRaw(resp));
    }
    const fold = chunk.fold;
    // Overlap is only what an EARLIER response folded into this chunk. A repeated
    // E inside this response is a duplicate record, and the fold refuses it.
    const foldedBefore = fold.tickCount > 0 ? fold.epochAt(fold.tickCount - 1) : undefined;
    for (const row of resp.TRC ?? []) {
      if (foldedBefore !== undefined && row.E <= foldedBefore && fold.tickForEpoch(row.E) >= 0) {
        this.overlapSkipped++;
        console.warn(
          `ZoneDerivation ${this.project}: tick ${row.E} already folded; skipped (${this.overlapSkipped} so far)`
        );
        continue;
      }
      fold.foldRaw(valuesOf(row), row.E);
    }
  }

  /** Once refused, always refused: a derivation never silently resumes. */
  private guard<T>(work: () => T): T {
    if (this.refused) {
      throw new ZoneFoldRefused(this.refused);
    }
    try {
      return work();
    } catch (e) {
      if (e instanceof ZoneFoldRefused) {
        this.refusal = this.refusal ?? e.message;
      }
      throw e;
    }
  }
}

// ---------------------------------------------------------------------------
// ZERO-EXTRA-BYTES PATH: derive from the display temperatures already loaded.
//
// Proven before this was built (2026-09-25):
//  - the float32 decode v/10f - 273.15f is ONE-TO-ONE over 0..65535 (65,536
//    distinct values, exhaustive);
//  - the shortest round-trip decimal the server writes parses back to the same
//    float32 for every value (exhaustive, 0 failures), and a float64 decode fails
//    the same check for 22,298 values, so the check can fail;
//  - on the real wire, S3077 metric: 411,539 display values inverted to exactly
//    the raw=1 stored values, 0 mismatches.
// So a metric, unthinned display row IS the stored row, recoverable exactly. Any
// row that is not (°F, thinned, or a value that does not invert) makes the whole
// load fall back to raw=1 chunks. Never a partial or approximate answer.
// ---------------------------------------------------------------------------

let decodeTable: Float32Array | null = null;

/** Every stored value's float32 decode, indexed by the stored value. Strictly increasing. */
function decodes(): Float32Array {
  if (!decodeTable) {
    decodeTable = new Float32Array(65536);
    for (let v = 0; v <= 65535; v++) decodeTable[v] = temperatureFromShortF32(v);
  }
  return decodeTable;
}

/** The stored ushort whose decode is exactly `celsius` (as float32), or -1. */
export function invertTemperature(celsius: number): number {
  const x = Math.fround(celsius);
  if (!Number.isFinite(x)) return -1;
  const t = decodes();
  let lo = 0;
  let hi = 65535;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = t[mid];
    if (v === x) return mid;
    if (v < x) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/** A display temperature payload, as far as this path reads it. */
export interface DisplayTemperaturePayload {
  TRC?: Array<{ E: number; TR: number[]; SIX?: number[] }>;
  CT: number;
  OT: number;
  MI: number;
  HeatInput?: RawTemperatureResponse['HeatInput'];
  heatInput?: RawTemperatureResponse['heatInput'];
  HWM?: number;
}

export type DisplayDerivation =
  | { ok: true; derivation: ZoneDerivation; rows: ZoneDataRow[] }
  | { ok: false; reason: string };

/**
 * Fold zones from display rows, inverting each value to its stored ushort.
 *
 * @param unitCodeSent the unitCode the display request ACTUALLY sent. Only 0
 *   (metric) is a stored-value decode; °F values can collide with the table.
 */
export function deriveFromDisplay(
  project: string,
  window: LinerWindow,
  payload: DisplayTemperaturePayload | null | undefined,
  unitCodeSent: number | null
): DisplayDerivation {
  if (unitCodeSent !== 0) {
    return { ok: false, reason: `display temperatures were requested in unitCode ${unitCodeSent}, not metric` };
  }
  const rows = payload?.TRC ?? [];
  if (!payload || rows.length === 0) {
    return { ok: false, reason: 'no display temperature rows' };
  }
  const width = window.end - window.start;
  const derivation = new ZoneDerivation(project, window);
  const stored = new Array<number>(width);
  const params = { RAW: 1, RWS: window.start, CT: payload.CT, OT: payload.OT, MI: payload.MI,
    HeatInput: payload.HeatInput, heatInput: payload.heatInput };
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const six = row.SIX;
    let at: (k: number) => number;
    if (six && six.length > 0) {
      // Windowed rows carry SIX even when nothing is thinned. Accept ONLY the
      // exact contiguous liner window.
      if (six.length !== width || row.TR.length !== width || six[0] !== window.start || six[width - 1] !== window.end - 1) {
        return { ok: false, reason: `display row ${i} is thinned or windowed differently from [${window.start}, ${window.end})` };
      }
      for (let k = 1; k < width; k++) {
        if (six[k] !== window.start + k) {
          return { ok: false, reason: `display row ${i} is not contiguous over the liner window` };
        }
      }
      at = (k) => row.TR[k];
    } else {
      if (row.TR.length < window.end) {
        return { ok: false, reason: `display row ${i} is shorter than the liner window` };
      }
      at = (k) => row.TR[window.start + k];
    }
    for (let k = 0; k < width; k++) {
      const v = invertTemperature(at(k));
      if (v < 0) {
        return { ok: false, reason: `display row ${i} sample ${window.start + k} (${at(k)}) is not a stored-value decode` };
      }
      stored[k] = v;
    }
    try {
      derivation.ingestChunk(0, { ...params, TRC: [{ E: row.E, TR: stored }] } as RawTemperatureResponse);
    } catch (e) {
      if (e instanceof ZoneFoldRefused) return { ok: false, reason: e.message };
      throw e;
    }
  }
  return { ok: true, derivation, rows: derivation.emitNewRows() };
}
